import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * DemoChatProvider - Provides demo data and playback control
 * 
 * Manages:
 * - Message visibility (which messages are shown)
 * - Playback phases (idle, typing, capturing, revealing)
 * - Typing simulation for user messages
 * - Photo capture simulation
 */

const DemoChatContext = createContext(null);

export const useDemoChat = () => useContext(DemoChatContext);

// Transform usecase message to app message format
const transformMessage = (msg, index, usecaseId) => {
  const metadata = msg.message_metadata || {};
  
  return {
    id: msg.id || `demo-msg-${usecaseId}-${index}`,
    chatId: `demo-chat-${usecaseId}`,
    role: msg.role,
    content: msg.content,
    createdAt: new Date(Date.now() - (1000 * 60 * (100 - index))).toISOString(),
    // StreamingMessage looks for message_metadata.imageUrl
    message_metadata: {
      ...metadata,
      // Ensure imageUrl is in message_metadata for StreamingMessage
      ...(metadata.imageUrl && { imageUrl: metadata.imageUrl }),
    },
    // Also set at top level as backup
    ...(metadata.imageUrl && { imageUrl: metadata.imageUrl }),
  };
};

export const DemoChatProvider = ({ 
  children, 
  usecase,
  onPlaybackComplete,
  autoPlay = true,
  playbackSpeed = 1,
}) => {
  // Playback state
  const [visibleMessageCount, setVisibleMessageCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [playbackPhase, setPlaybackPhase] = useState('welcome'); // welcome, idle, typing, viewfinder, captured, revealing, clicking
  const [typingText, setTypingText] = useState('');
  const [showCameraFlash, setShowCameraFlash] = useState(false);
  const [flashKey, setFlashKey] = useState(0); // Unique key for flash animation
  const [demoExpandedCardIndex, setDemoExpandedCardIndex] = useState(null);
  const [demoExpandedMessageId, setDemoExpandedMessageId] = useState(null); // Which message's card is expanded
  const [demoClickingCardId, setDemoClickingCardId] = useState(null); // Card ID being "clicked" by demo user
  const [cameraViewfinderImage, setCameraViewfinderImage] = useState(null); // Image URL for fake camera viewfinder
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false); // Show loading eyes before AI response
  const [demoSuppressedFormCardIds, setDemoSuppressedFormCardIds] = useState(new Set());
  
  // Guard ref to prevent double-execution of sequences
  const processingMessageRef = useRef(null);
  
  const playbackTimerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const phaseTimerRef = useRef(null); // Separate ref for phase transitions

  const allMessages = usecase?.messages || [];
  const totalMessages = allMessages.length;
  const isComplete = visibleMessageCount >= totalMessages;

  // Helper: Find which card a user message is responding to
  const findRespondingCard = useCallback((userMsg, prevAssistantMsg) => {
    if (!userMsg || !prevAssistantMsg) return null;
    const cards = prevAssistantMsg.message_metadata?.interactive_cards || [];
    if (cards.length === 0) return null;
    
    const content = userMsg.content || '';
    const metadata = userMsg.message_metadata || {};
    
    // Check for explicit card reference in metadata
    if (metadata.action_card_id) {
      return cards.find(c => c.id === metadata.action_card_id) || null;
    }
    
    // Check for photo response - matches observe_photo cards
    if (content.startsWith('Photo for:') || metadata.imageUrl) {
      const photoCard = cards.find(c => c.type === 'observe_photo' || c.type === 'observe');
      if (photoCard) return photoCard;
    }
    
    // Check for decision response
    if (content.startsWith('Decision:')) {
      return cards.find(c => c.type === 'decision') || null;
    }
    
    // Check for form completion
    if (content.includes('Completed') || content.includes('Project Setup')) {
      return cards.find(c => c.type === 'observe_form') || null;
    }
    
    // Check for plan acceptance
    if (content.toLowerCase().includes('plan accepted') || content.toLowerCase().includes('accept')) {
      return cards.find(c => c.type === 'plan_update') || null;
    }
    
    // Check for guide card option selection (e.g., "Selected: Done—I see steam rising")
    if (content.startsWith('Selected:')) {
      return cards.find(c => c.type === 'guide') || null;
    }
    
    // Default: return first actionable card
    return cards.find(c => 
      c.type === 'observe_photo' || 
      c.type === 'observe_form' || 
      c.type === 'decision' || 
      c.type === 'plan_update' ||
      c.type === 'guide'
    ) || null;
  }, []);

  // Get currently visible messages in app format
  const visibleMessages = allMessages
    .slice(0, visibleMessageCount)
    .map((msg, i) => transformMessage(msg, i, usecase?.id));
  
  // Get the next message (for typing simulation)
  const nextMessage = allMessages[visibleMessageCount];

  // Track which objective_match cards have been "confirmed" by demo user
  const [demoConfirmedMatchIds, setDemoConfirmedMatchIds] = useState([]);
  
  // Compute which cards should be marked as "handled" based on message progression
  // A card is "handled" if there's a subsequent user message after the message containing it
  // EXCEPT: objective_match cards need explicit confirmation (they don't trigger user messages)
  const demoHandledCardIds = React.useMemo(() => {
    const handledIds = [];
    
    for (let i = 0; i < visibleMessageCount; i++) {
      const msg = allMessages[i];
      if (msg?.role === 'assistant' && msg.message_metadata?.interactive_cards) {
        // Check if there's a user message after this assistant message
        const hasFollowupUserMessage = allMessages
          .slice(i + 1, visibleMessageCount)
          .some(m => m.role === 'user');
        
        if (hasFollowupUserMessage) {
          // All cards in this assistant message are "handled"
          // EXCEPT objective_match cards - they need explicit confirmation
          msg.message_metadata.interactive_cards.forEach(card => {
            if (card.id && card.type !== 'objective_match') {
              handledIds.push(card.id);
            }
          });
        }
      }
    }
    
    // Add explicitly confirmed objective_match cards
    return [...handledIds, ...demoConfirmedMatchIds];
  }, [allMessages, visibleMessageCount, demoConfirmedMatchIds]);

  // Demo UX: keep observe_form cards expanded when first shown
  useEffect(() => {
    if (!allMessages || visibleMessageCount <= 0) return;
    if (demoExpandedCardIndex !== null || demoExpandedMessageId !== null) return;
    const lastVisibleMsg = allMessages[visibleMessageCount - 1];
    if (!lastVisibleMsg || lastVisibleMsg.role !== 'assistant') return;
    const cards = lastVisibleMsg.message_metadata?.interactive_cards || [];
    if (!cards.length) return;
    const formIndex = cards.findIndex(
      (card) =>
        card.type === 'observe_form' ||
        (card.type === 'observe' && Array.isArray(card.form_fields) && card.form_fields.length > 0),
    );
    if (formIndex >= 0) {
      const formCardId = cards[formIndex]?.id;
      const isAlreadyHandled =
        formCardId && demoHandledCardIds && demoHandledCardIds.includes(formCardId);
      const isSuppressed = formCardId && demoSuppressedFormCardIds.has(formCardId);
      if (isAlreadyHandled || isSuppressed) return;
      setDemoExpandedCardIndex(formIndex);
      setDemoExpandedMessageId(lastVisibleMsg.id || null);
    }
  }, [
    allMessages,
    visibleMessageCount,
    demoExpandedCardIndex,
    demoExpandedMessageId,
    demoHandledCardIds,
    demoSuppressedFormCardIds,
  ]);

  // Reset when usecase changes
  useEffect(() => {
    setVisibleMessageCount(0);
    setIsPlaying(autoPlay);
    setPlaybackPhase('idle'); // Start immediately - header is inline now
    setTypingText('');
    setShowCameraFlash(false);
    setDemoExpandedCardIndex(null);
    setDemoExpandedMessageId(null);
    setDemoClickingCardId(null);
    setCameraViewfinderImage(null);
    setDemoConfirmedMatchIds([]); // Clear confirmed matches
    setDemoSuppressedFormCardIds(new Set());
    processingMessageRef.current = null; // Clear processing guard
    if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    
    // Small delay before first message to show header
    if (autoPlay) {
      phaseTimerRef.current = setTimeout(() => {
        setPlaybackPhase('idle'); // Trigger first message
      }, 1200 / playbackSpeed); // Brief pause to see header
    }
  }, [usecase?.id, autoPlay, playbackSpeed]);

  // Typing animation
  const startTyping = useCallback((text, onComplete) => {
    if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    
    setTypingText('');
    setPlaybackPhase('typing');
    let index = 0;
    
    typingTimerRef.current = setInterval(() => {
      if (index < text.length) {
        setTypingText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        // Brief pause after typing completes
        setTimeout(() => {
          setTypingText('');
          onComplete();
        }, 300 / playbackSpeed);
      }
    }, 25 / playbackSpeed);
  }, [playbackSpeed, setDemoSuppressedFormCardIds]);

  // Photo card interaction - handles the entire "Take Photo" flow
  // Expand card → view at top → scroll to button → click → viewfinder → photo → chat
  const simulatePhotoCardClick = useCallback((cardId, cardIndex, messageId, imageUrl, onComplete) => {
    // Stage 1: Expand the card and stay at top to view image/instructions (2000ms)
    setPlaybackPhase('viewing'); // Scrolls card to top of phone view
    setDemoExpandedCardIndex(cardIndex);
    setDemoExpandedMessageId(messageId);
    
    phaseTimerRef.current = setTimeout(() => {
      // Stage 2: Slowly scroll down to the "Take Photo" button (2000ms for scroll animation)
      setPlaybackPhase('scrollingToButton');
      
      phaseTimerRef.current = setTimeout(() => {
        // Stage 3: Pause at button before clicking (800ms) - user sees the button
        phaseTimerRef.current = setTimeout(() => {
          // Stage 4: Brief button feedback (400ms) - user sees they clicked
          setPlaybackPhase('clicking');
          setDemoClickingCardId(cardId);
          
          phaseTimerRef.current = setTimeout(() => {
            // Stage 5: Collapse card AND open viewfinder simultaneously
            setDemoClickingCardId(null);
            setDemoExpandedCardIndex(null);
            setDemoExpandedMessageId(null);
            setPlaybackPhase('viewfinder');
            setCameraViewfinderImage(imageUrl);
            
            // Stage 6: Hold viewfinder (1500ms) - user sees the camera view
            phaseTimerRef.current = setTimeout(() => {
              // Stage 7: Flash effect - "taking the photo"
              setFlashKey(k => k + 1);
              setShowCameraFlash(true);
              
              // Stage 8: Flash visible (400ms)
              phaseTimerRef.current = setTimeout(() => {
                setShowCameraFlash(false);
                setCameraViewfinderImage(null);
                setPlaybackPhase('captured');
                
                // Stage 9: Show "Photo captured!" state briefly (600ms) before adding to chat
                phaseTimerRef.current = setTimeout(() => {
                  onComplete();
                }, 600 / playbackSpeed);
              }, 400);
            }, 1500 / playbackSpeed);
          }, 400 / playbackSpeed);
        }, 800 / playbackSpeed); // Pause at button before clicking
      }, 2000 / playbackSpeed); // Time for slow scroll animation (1800ms) + buffer
    }, 2000 / playbackSpeed); // Time to view image and instructions at top
  }, [playbackSpeed]);

  // Form card interaction - expand, show typing in fields, then submit
  const simulateFormCardClick = useCallback((cardId, cardIndex, messageId, onComplete) => {
    // Stage 1: Expand the form card (1000ms to see it expand)
    setPlaybackPhase('scrolling');
    setDemoExpandedCardIndex(cardIndex);
    setDemoExpandedMessageId(messageId);
    
    phaseTimerRef.current = setTimeout(() => {
      // Stage 2: Simulate "filling" the form (1800ms - appears as if typing)
      setPlaybackPhase('typing');
      
      phaseTimerRef.current = setTimeout(() => {
        // Stage 3: Click submit (800ms)
        setPlaybackPhase('clicking');
        setDemoClickingCardId(cardId);
        
        phaseTimerRef.current = setTimeout(() => {
          setDemoClickingCardId(null);
          setDemoExpandedCardIndex(null); // Collapse after submit
          setDemoExpandedMessageId(null);
          if (cardId) {
            setDemoSuppressedFormCardIds(prev => {
              const next = new Set(prev);
              next.add(cardId);
              return next;
            });
          }
          onComplete();
        }, 800 / playbackSpeed);
      }, 1800 / playbackSpeed);
    }, 1000 / playbackSpeed);
  }, [playbackSpeed]);

  // Simulate clicking a card button (for macro cards like decision, plan_update)
  const simulateCardClick = useCallback((cardId, onComplete) => {
    // First expand the card briefly so user sees what's being clicked
    setPlaybackPhase('scrolling');
    
    phaseTimerRef.current = setTimeout(() => {
      // Now click the option/button
      setPlaybackPhase('clicking');
      setDemoClickingCardId(cardId);
      
      // Show "clicked/loading" state on button (800ms - let viewer see the button state)
      phaseTimerRef.current = setTimeout(() => {
        setDemoClickingCardId(null);
        // Brief pause after click before advancing (400ms)
        phaseTimerRef.current = setTimeout(onComplete, 400 / playbackSpeed);
      }, 800 / playbackSpeed);
    }, 400 / playbackSpeed); // Brief pause to "see" the card
  }, [playbackSpeed]);

  // Advance to next message
  const advanceMessage = useCallback(() => {
    if (visibleMessageCount >= totalMessages) {
      setIsPlaying(false);
      setPlaybackPhase('idle');
      processingMessageRef.current = null; // Clear guard
      onPlaybackComplete?.();
      return;
    }
    processingMessageRef.current = null; // Clear guard to allow next message
    setPlaybackPhase('revealing');
    setVisibleMessageCount(prev => prev + 1);
  }, [visibleMessageCount, totalMessages, onPlaybackComplete]);

  // Auto-playback logic with realistic simulation
  useEffect(() => {
    if (!isPlaying || isComplete) return;
    if (playbackPhase !== 'idle') return; // Don't start new action while animating

    const nextMsg = allMessages[visibleMessageCount];
    if (!nextMsg) {
      // Initial delay before starting
      if (visibleMessageCount === 0) {
        playbackTimerRef.current = setTimeout(() => {
          advanceMessage();
        }, 1200 / playbackSpeed);
      }
      return () => clearTimeout(playbackTimerRef.current);
    }
    
    // Guard against double-processing the same message
    const msgKey = `${visibleMessageCount}-${nextMsg.id}`;
    if (processingMessageRef.current === msgKey) {
      return; // Already processing this message
    }
    processingMessageRef.current = msgKey;

    if (nextMsg.role === 'user') {
      const hasPhoto = nextMsg.message_metadata?.imageUrl;
      
      // Find the previous assistant message to check for cards to click
      const prevMsgIndex = visibleMessageCount - 1;
      const prevMsg = prevMsgIndex >= 0 ? allMessages[prevMsgIndex] : null;
      const respondingCard = prevMsg?.role === 'assistant' ? findRespondingCard(nextMsg, prevMsg) : null;
      
      // Card types that auto-send messages (no typing needed):
      // - "Macro" cards: decision, plan_update - click sends message instantly
      // - Form cards: observe_form - expand, fill, submit
      // - Photo cards: observe_photo/observe - click takes photo, message auto-generated
      const isMacroCard = respondingCard && [
        'decision',      // "Decision: X" is auto-sent
        'plan_update',   // Plan acceptance is auto-sent
        'guide',         // Guide option selection is auto-sent
      ].includes(respondingCard.type);
      
      const isFormCard = respondingCard && respondingCard.type === 'observe_form';
      
      const isPhotoCard = respondingCard && [
        'observe_photo', // Photo cards auto-generate "Photo for: X" message
        'observe',       // Legacy observe cards
      ].includes(respondingCard.type);
      
      // Flow sequences:
      // - Macro cards: click -> immediate message (no typing, no photo)
      // - Photo cards: click -> flash -> immediate message (no typing)
      // - Regular messages: typing -> advance
      
      const doAdvanceAndScroll = () => {
        advanceMessage();
        phaseTimerRef.current = setTimeout(() => {
          setPlaybackPhase('idle');
        }, 1200 / playbackSpeed); // Pause after user actions (photo, form, etc)
      };
      
      const doTypingAndAdvance = () => {
        startTyping(nextMsg.content, () => {
          advanceMessage();
          phaseTimerRef.current = setTimeout(() => {
            setPlaybackPhase('idle');
          }, 500 / playbackSpeed);
        });
      };
      
      // Get the image URL for photo capture simulation
      const photoUrl = nextMsg.message_metadata?.imageUrl;
      
      // Find the index of the responding card in the message's cards array
      const prevCards = prevMsg?.message_metadata?.interactive_cards || [];
      const cardIndex = respondingCard ? prevCards.findIndex(c => c.id === respondingCard.id) : -1;
      
      // Determine the correct sequence based on card type and content
      if (respondingCard) {
        // The card is in the previous assistant message
        const prevMsgId = prevMsg?.id;
        
        if (isMacroCard) {
          // Macro card: click animation -> immediate message (no typing)
          simulateCardClick(respondingCard.id, doAdvanceAndScroll);
        } else if (isFormCard) {
          // Form card: expand -> typing simulation -> submit
          simulateFormCardClick(respondingCard.id, cardIndex, prevMsgId, doAdvanceAndScroll);
        } else if (isPhotoCard && hasPhoto) {
          // Photo card flow:
          // 1. Expand card to see instructions
          // 2. Scroll to "Take Photo" button
          // 3. Click button
          // 4. Viewfinder opens (card collapses)
          // 5. Photo taken (flash)
          // 6. Photo appears in chat
          simulatePhotoCardClick(respondingCard.id, cardIndex, prevMsgId, photoUrl, doAdvanceAndScroll);
        } else {
          // Other card types or photo card without photo - show typing
          simulateCardClick(respondingCard.id, doTypingAndAdvance);
        }
      } else if (hasPhoto) {
        // Photo without card context (e.g. user just takes a photo from input)
        // Just show viewfinder directly
        setPlaybackPhase('viewfinder');
        setCameraViewfinderImage(photoUrl);
        phaseTimerRef.current = setTimeout(() => {
          setFlashKey(k => k + 1);
          setShowCameraFlash(true);
          phaseTimerRef.current = setTimeout(() => {
            setShowCameraFlash(false);
            setCameraViewfinderImage(null);
            doAdvanceAndScroll();
          }, 400);
        }, 1800 / playbackSpeed);
      } else {
        // Regular user message - show typing
        doTypingAndAdvance();
      }
    } else {
      // Assistant message - show thinking indicator briefly, then reveal
      setPlaybackPhase('thinking');
      setShowThinkingIndicator(true);
      
      // Show thinking indicator for 600-900ms depending on content
      const cards = nextMsg.message_metadata?.interactive_cards || [];
      const hasCards = cards.length > 0;
      const thinkTime = hasCards ? 900 : 600;
      
      // Find objective_match cards that need confirmation
      const objectiveMatchCards = cards.filter(c => 
        c.type === 'objective_match' && 
        Array.isArray(c.actions) && 
        c.actions.length > 0
      );
      
      phaseTimerRef.current = setTimeout(() => {
        setShowThinkingIndicator(false);
        advanceMessage();
        
        const contentLength = nextMsg.content?.length || 0;
        
        // Different pause times based on content complexity (faster tempo)
        // - Photo cards have instructions to read: 3500ms
        // - Other cards: 2500ms  
        // - Long text (>200 chars): 2200ms
        // - Short text: 1600ms
        const hasPhotoCard = cards.some(c => c.type === 'observe_photo' || c.type === 'observe');
        let viewTime;
        if (hasPhotoCard) {
          viewTime = 3500; // Photo cards need time to expand and read instructions
        } else if (hasCards) {
          viewTime = 2500; // Cards need time to render and be seen
        } else if (contentLength > 200) {
          viewTime = 2200; // Longer text needs more reading time
        } else {
          viewTime = 1600; // Short messages
        }
        
        // If there are objective_match cards, simulate clicking "Accept Match" after view time
        if (objectiveMatchCards.length > 0) {
          phaseTimerRef.current = setTimeout(() => {
            // Click each objective_match card's confirm action
            let clickIndex = 0;
            const clickNextMatch = () => {
              if (clickIndex >= objectiveMatchCards.length) {
                // All matches confirmed, go idle
                setDemoClickingCardId(null);
                phaseTimerRef.current = setTimeout(() => {
                  setPlaybackPhase('idle');
                }, 400 / playbackSpeed);
                return;
              }
              
              const matchCard = objectiveMatchCards[clickIndex];
              const confirmAction = matchCard.actions.find(a => a.type === 'confirm_match');
              
              if (confirmAction) {
                // Show clicking state
                setPlaybackPhase('clicking');
                setDemoClickingCardId(matchCard.id);
                
                phaseTimerRef.current = setTimeout(() => {
                  // Mark as confirmed
                  setDemoConfirmedMatchIds(prev => [...prev, matchCard.id]);
                  setDemoClickingCardId(null);
                  clickIndex++;
                  
                  // Small pause before next click
                  phaseTimerRef.current = setTimeout(clickNextMatch, 300 / playbackSpeed);
                }, 600 / playbackSpeed);
              } else {
                clickIndex++;
                clickNextMatch();
              }
            };
            
            clickNextMatch();
          }, viewTime / playbackSpeed);
        } else {
          phaseTimerRef.current = setTimeout(() => {
            setPlaybackPhase('idle');
          }, viewTime / playbackSpeed);
        }
      }, thinkTime / playbackSpeed);
    }

    // Don't clear phaseTimerRef here - it needs to complete to advance playback
  }, [isPlaying, isComplete, playbackPhase, visibleMessageCount, allMessages, playbackSpeed, advanceMessage, startTyping, findRespondingCard, simulateCardClick, simulatePhotoCardClick, simulateFormCardClick]);

  // Control functions
  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);
  const reset = useCallback(() => {
    setVisibleMessageCount(0);
    setIsPlaying(autoPlay);
  }, [autoPlay]);
  const skipToEnd = useCallback(() => {
    setVisibleMessageCount(totalMessages);
    setIsPlaying(false);
  }, [totalMessages]);
  const goToMessage = useCallback((index) => {
    setVisibleMessageCount(Math.min(Math.max(0, index + 1), totalMessages));
  }, [totalMessages]);

  // Context value
  const contextValue = {
    // Playback state
    isPlaying,
    isComplete,
    visibleMessageCount,
    totalMessages,
    playbackPhase,
    
    // Simulation state
    typingText,
    showCameraFlash,
    flashKey, // Unique key for each flash animation
    cameraViewfinderImage, // Image URL for fake camera viewfinder overlay
    nextMessage,
    demoExpandedCardIndex,
    demoExpandedMessageId, // Which message's card is expanded
    setDemoExpandedCardIndex,
    setDemoExpandedMessageId,
    demoHandledCardIds,
    demoConfirmedMatchIds, // Objective_match cards that have been "confirmed" in demo
    demoClickingCardId, // Card being "clicked" by demo user
    showThinkingIndicator, // Show loading eyes before AI response
    
    // Control functions
    play,
    pause,
    reset,
    skipToEnd,
    goToMessage,
    advanceMessage,
    
    // Data access
    usecase,
    visibleMessages,
    allMessages: allMessages.map((msg, i) => transformMessage(msg, i, usecase?.id)),
  };

  return (
    <DemoChatContext.Provider value={contextValue}>
      {children}
    </DemoChatContext.Provider>
  );
};

export default DemoChatProvider;
